import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Checkbox, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button, Skeleton } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import { AuthContext } from '../utils/AuthContext';
import TableRatesRow from './TableRatesRow';
import { getQuotation } from '../services/CompanyQuotationService';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';

const CignaCareConnect_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, paymentFrequency, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  cignaCareConnectSpecific, setCignaCareConnectSpecific, loadings, setLoadings , insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'cigna_care_connect';

  const inpatientOptions = appConfig?.content?.specifics?.cigna_care_connect?.inpatient_options || [];
  const outpatientOptions = appConfig?.content?.specifics?.cigna_care_connect?.outpatient_options || [];
  const maternityOptions = appConfig?.content?.specifics?.cigna_care_connect?.maternity_options || [];
  const preventiveOptions = appConfig?.content?.specifics?.cigna_care_connect?.preventive_options || [];
  const dentalOptions = appConfig?.content?.specifics?.cigna_care_connect?.dental_options || [];
  const visionOptions = appConfig?.content?.specifics?.cigna_care_connect?.vision_options || [];
  const gst = appConfig?.content?.specifics?.cigna_care_connect?.gst ?? 0;

  const [inpatientType, setInpatientType] = useState(inpatientOptions?.[0]);
  const [outpatientType, setOutpatientType] = useState(outpatientOptions?.[0]);
  const [maternityType, setMaternityType] = useState(maternityOptions?.[0]);
  const [preventiveType, setPreventiveType] = useState(preventiveOptions?.[0]);
  const [dentalType, setDentalType] = useState(dentalOptions?.[0]);
  const [visionType, setVisionType] = useState(visionOptions?.[0]);


  const specific_area_of_coverages = appConfig?.content?.cigna_care_connect_area_of_coverage;

  const currencies = appConfig?.content?.currencies || [];

  useEffect(() => {
    cignaCareConnectSpecific['inpatient']['type'] = inpatientOptions?.[0];
    cignaCareConnectSpecific['outpatient']['type'] = outpatientOptions?.[0];
    cignaCareConnectSpecific['maternity']['type'] = maternityOptions?.[0];
    cignaCareConnectSpecific['preventive']['type'] = preventiveOptions?.[0];
    cignaCareConnectSpecific['dental']['type'] = dentalOptions?.[0];
    cignaCareConnectSpecific['vision'] = cignaCareConnectSpecific['vision'] || {};
    cignaCareConnectSpecific['vision']['type'] = visionOptions?.[0];

    setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
  }, []);

  useEffect(() => {
    computedRates[id] = getComputedRatesForPlanComparison();
    setComputedRates(computedRates);
  }, [response, discounts, loadings, selectedPlans]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
      addSelectedOption(id, 'inpatient_type', inpatientType);
      addSelectedOption(id, 'outpatient_type', outpatientType);
      addSelectedOption(id, 'maternity_type', maternityType);
      addSelectedOption(id, 'preventive_type', preventiveType);
      addSelectedOption(id, 'dental_type', dentalType);
      addSelectedOption(id, 'vision_type', visionType);
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], discounts[id], loadings[id], inpatientType, outpatientType, maternityType, preventiveType, dentalType, visionType]);

  useEffect(() => {
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'inpatient_type', (option_value) => {
      setInpatientType(option_value);

      cignaCareConnectSpecific['inpatient']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });

    setViewSelectedOption(id, 'outpatient_type', (option_value) => {
      setOutpatientType(option_value);

      cignaCareConnectSpecific['outpatient']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });

    setViewSelectedOption(id, 'maternity_type', (option_value) => {
      setMaternityType(option_value);

      cignaCareConnectSpecific['maternity']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });

    setViewSelectedOption(id, 'preventive_type', (option_value) => {
      setPreventiveType(option_value);

      cignaCareConnectSpecific['preventive']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });

    setViewSelectedOption(id, 'dental_type', (option_value) => {
      setDentalType(option_value);

      cignaCareConnectSpecific['dental']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });

    setViewSelectedOption(id, 'vision_type', (option_value) => {
      setVisionType(option_value);

      cignaCareConnectSpecific['vision']['type'] = option_value;
      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });
    });
  }, [selectedOptions]);

  function getComputedRatesForPlanComparison() {
    if (!response) return;

    const rates = response.rates;

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const prestige = computeTotalPremiumPerClient(i, 'prestige');
      const elite = computeTotalPremiumPerClient(i, 'elite');
      const executive = computeTotalPremiumPerClient(i, 'executive');

      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'prestige': prestige,
        'elite': elite,
        'executive': executive,
      })
    }

    return computedRates;
  }

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'executive_inpatient': computedRate(rates[i].executive_inpatient) * totalPercent,
        'executive_outpatient': computedRate(rates[i].executive_outpatient) * totalPercent,
        'executive_maternity': computedRate(rates[i].executive_maternity) * totalPercent,
        'executive_preventive': computedRate(rates[i].executive_preventive) * totalPercent,
        'executive_dental': computedRate(rates[i].executive_dental) * totalPercent,
        'executive_vision': computedRate(rates[i].executive_vision) * totalPercent,

        'elite_inpatient': computedRate(rates[i].elite_inpatient) * totalPercent,
        'elite_outpatient': computedRate(rates[i].elite_outpatient) * totalPercent,
        'elite_maternity': computedRate(rates[i].elite_maternity) * totalPercent,
        'elite_preventive': computedRate(rates[i].elite_preventive) * totalPercent,
        'elite_dental': computedRate(rates[i].elite_dental) * totalPercent,
        'elite_vision': computedRate(rates[i].elite_vision) * totalPercent,

        'prestige_inpatient': computedRate(rates[i].prestige_inpatient) * totalPercent,
        'prestige_outpatient': computedRate(rates[i].prestige_outpatient) * totalPercent,
        'prestige_maternity': computedRate(rates[i].prestige_maternity) * totalPercent,
        'prestige_preventive': computedRate(rates[i].prestige_preventive) * totalPercent,
        'prestige_dental': computedRate(rates[i].prestige_dental) * totalPercent,
        'prestige_vision': computedRate(rates[i].prestige_vision) * totalPercent
      });
    }

    return computedRates;
  }

  function computeCignaCareConnectRates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'executive_inpatient': computedRate(rates[i].executive_inpatient),
        'executive_outpatient': computedRate(rates[i].executive_outpatient),
        'executive_maternity': computedRate(rates[i].executive_maternity),
        'executive_preventive': computedRate(rates[i].executive_preventive),
        'executive_dental': computedRate(rates[i].executive_dental),
        'executive_vision': computedRate(rates[i].executive_vision),

        'elite_inpatient': computedRate(rates[i].elite_inpatient),
        'elite_outpatient': computedRate(rates[i].elite_outpatient),
        'elite_maternity': computedRate(rates[i].elite_maternity),
        'elite_preventive': computedRate(rates[i].elite_preventive),
        'elite_dental': computedRate(rates[i].elite_dental),
        'elite_vision': computedRate(rates[i].elite_vision),

        'prestige_inpatient': computedRate(rates[i].prestige_inpatient),
        'prestige_outpatient': computedRate(rates[i].prestige_outpatient),
        'prestige_maternity': computedRate(rates[i].prestige_maternity),
        'prestige_preventive': computedRate(rates[i].prestige_preventive),
        'prestige_dental': computedRate(rates[i].prestige_dental),
        'prestige_vision': computedRate(rates[i].prestige_vision)
      })
    }

    return computedRates;
  }

  function computedRate(rate) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }

    let total = rate;

    // MHD loadings
    //total = total * (1 + MHD_LoadingValue);

    // company discount
    //total = total * (1 - companyDiscountValue);

    // group size discount
    //total = total * (1 - groupSizeValue);

    // const payment_frequency_divided = getPaymentFrequencyDivided();

    // total /= payment_frequency_divided;


    return total;
  }

  function computeTotalPremium(plan) {
    let total = 0;

    for (let i = 0; i < clients.length; i++) {
      const totalPerClient = computeTotalPremiumPerClient(i, plan);
      if (!isNaN(totalPerClient)) {
        total += totalPerClient;
      }
    }

    return total;
  }

  function computeTotalPremiumPerClient(clientIndex, plan) {
    const computedRates = getComputedRates();

    if (!computedRates) return 0;

    const rates = computedRates[clientIndex];

    if (!rates) return 0;

    let total = 0;

    if (selectedPlans.includes('cigna_care_connect_' + plan) && !isNaN(rates[plan + '_inpatient'])) {
      total += rates[plan + '_inpatient'];
    }
    if (selectedPlans.includes('cigna_care_connect_' + plan) && selectedPlans.includes('cigna_care_connect_outpatient') && !isNaN(rates[plan + '_outpatient'])) {
      total += rates[plan + '_outpatient'];
    }
    if (selectedPlans.includes('cigna_care_connect_' + plan) && selectedPlans.includes('cigna_care_connect_maternity') && !isNaN(rates[plan + '_maternity'])) {
      total += rates[plan + '_maternity'];
    }
    if (selectedPlans.includes('cigna_care_connect_' + plan) && selectedPlans.includes('cigna_care_connect_preventive') && !isNaN(rates[plan + '_preventive'])) {
      total += rates[plan + '_preventive'];
    }
    if (selectedPlans.includes('cigna_care_connect_' + plan) && selectedPlans.includes('cigna_care_connect_dental') && !isNaN(rates[plan + '_dental'])) {
      total += rates[plan + '_dental'];
    }
    if (selectedPlans.includes('cigna_care_connect_' + plan) && selectedPlans.includes('cigna_care_connect_vision') && !isNaN(rates[plan + '_vision'])) {
      total += rates[plan + '_vision'];
    }


    return total *= 1 + gst;
  }

  function formatRate(rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getCurrencySign() {
    const currency = getCurrency();

    if (currency === 'USD') {
      return '$';
    } else if (currency === 'EURO') {
      return '€';
    } else if (currency === 'GBP') {
      return '£';
    } else if (currency === 'SGD') {
      return 'S$';
    }

    return '';
  }

  function getCurrency() {
    const computedRates = getComputedRates();

    if (!computedRates) return '';

    if (computedRates.length === 0) { return '' }

    return computedRates[0].currency;
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#00629b', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#00629b',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            // width: 100,
            height: 60
          }}>Cigna Care Connect</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 1.5, py: 0.5 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/cigna-logo.svg`}
              alt="Cigna"
              style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_prestige' />} label="Prestige" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_elite' />} label="Elite" />
              <FormControlLabel sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }} control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_executive' />} label="Executive" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Benefits</Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100 }}>Inpatient</Typography>}
                    control={<Checkbox size="small" checked onChange={(e) => e.preventDefault()} />} />
                  <Select
                    size='small'
                    value={inpatientType}
                    onChange={(e) => {
                      setInpatientType(e.target.value);

                      cignaCareConnectSpecific['inpatient']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      inpatientOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100, cursor: 'pointer' }}>Outpatient</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_outpatient' />} />
                  <Select
                    size='small'
                    value={outpatientType}
                    onChange={(e) => {
                      setOutpatientType(e.target.value);

                      cignaCareConnectSpecific['outpatient']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      outpatientOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100, cursor: 'pointer' }}>Maternity</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_maternity' />} />
                  <Select
                    size='small'
                    value={maternityType}
                    onChange={(e) => {
                      setMaternityType(e.target.value);

                      cignaCareConnectSpecific['maternity']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      maternityOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100, cursor: 'pointer' }}>Preventive</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_preventive' />} />
                  <Select
                    size='small'
                    value={preventiveType}
                    onChange={(e) => {
                      setPreventiveType(e.target.value);

                      cignaCareConnectSpecific['preventive']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      preventiveOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100, cursor: 'pointer' }}>Dental</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_dental' />} />
                  <Select
                    size='small'
                    value={dentalType}
                    onChange={(e) => {
                      setDentalType(e.target.value);

                      cignaCareConnectSpecific['dental']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      dentalOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    label={<Typography fontSize={13} sx={{ width: 100, cursor: 'pointer' }}>Vision</Typography>}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='cigna_care_connect_vision' />} />
                  <Select
                    size='small'
                    value={visionType}
                    onChange={(e) => {
                      setVisionType(e.target.value);

                      cignaCareConnectSpecific['vision']['type'] = e.target.value;
                      setCignaCareConnectSpecific({ ...cignaCareConnectSpecific });

                      addToPendingTasks([id]);
                    }}
                    sx={{ fontSize: 12, height: 32, width: '160px' }}
                    fullWidth
                    disabled={loadingStatus[id]}
                  >
                    {
                      visionOptions.map(option => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>{option}</MenuItem>
                      ))
                    }

                  </Select>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Area of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px' }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage} sx={{ fontSize: 12 }}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });



                    // clearResponse();

                    // setDeductibles(prev => {
                    //   return {
                    //     ...prev,
                    //     [id]: {
                    //       label: getSafe(() => appConfig.content.deductibles.company[specificCurrency[id]][0]),
                    //       deductible_index: 'D0'
                    //     }
                    //   };
                    // });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["SGD"].map(currency => (
                      <MenuItem key={currency} value={currency} sx={{ fontSize: 12 }}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency} sx={{ fontSize: 12 }}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id]}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id]}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          {/* <div style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <Button
              size='small'
              sx={{ mb: 1 }}
              onClick={() => {
                var node = document.getElementById('table-cigna-care-connect-rates');

                node.style = 'width: auto;';

                domtoimage.toBlob(node).then(function (blob) {
                  // Create ClipboardItem with blob and its type, and add to an array
                  const data = [new ClipboardItem({ [blob.type]: blob })];
                  // Write the data to the clipboard
                  navigator.clipboard.write(data);

                  setShowImageCopy(true);

                  node.style = 'width: 100%;';
                })
                  .catch(function (error) {
                    console.error('oops, something went wrong!', error);
                    node.style = 'width: 100%;';
                  });
              }}
              endIcon={<ContentCopyIcon />}
            >Copy Image</Button>
          </div> */}
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7 }}>
            <CopyExcelDataButton
              elementId='table-cigna-care-connect-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-cigna-care-connect-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto'}}>
            <Table id='table-cigna-care-connect-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#00629B', color: '#fff' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>

              </TableHead>
              <TableBody>
                {
                  selectedPlans.includes('cigna_care_connect_prestige') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#b9f6ca', color: '#000' }}>Prestige</TableCell>
                  </TableRow>
                }

                <TableRatesRow
                  planId='cigna_care_connect_prestige_inpatient'
                  planName='prestige_inpatient'
                  planLabel='Inpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_prestige_outpatient'
                  planName='prestige_outpatient'
                  planLabel='Outpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige') && selectedPlans.includes('cigna_care_connect_outpatient')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_prestige_maternity'
                  planName='prestige_maternity'
                  planLabel='Maternity'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige') && selectedPlans.includes('cigna_care_connect_maternity')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_prestige_preventive'
                  planName='prestige_preventive'
                  planLabel='Preventive'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige') && selectedPlans.includes('cigna_care_connect_preventive')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_prestige_dental'
                  planName='prestige_dental'
                  planLabel='Dental'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige') && selectedPlans.includes('cigna_care_connect_dental')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_prestige_vision'
                  planName='prestige_vision'
                  planLabel='Vision'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_prestige') && selectedPlans.includes('cigna_care_connect_vision')}
                />

                {
                  selectedPlans.includes('cigna_care_connect_prestige') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#dfe3ec' }}>Total Premium Incl. GST {gst * 100}%</TableCell>
                    {
                      clients.map((client, idx) => {
                        return <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx, 'prestige'))}</TableCell>
                      })
                    }
                    <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremium('prestige'))}</TableCell>
                  </TableRow>
                }



                {
                  selectedPlans.includes('cigna_care_connect_elite') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#b9f6ca', color: '#000' }}>Elite</TableCell>
                  </TableRow>
                }

                <TableRatesRow
                  planId='cigna_care_connect_elite_inpatient'
                  planName='elite_inpatient'
                  planLabel='Inpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_elite_outpatient'
                  planName='elite_outpatient'
                  planLabel='Outpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite') && selectedPlans.includes('cigna_care_connect_outpatient')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_elite_maternity'
                  planName='elite_maternity'
                  planLabel='Maternity'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite') && selectedPlans.includes('cigna_care_connect_maternity')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_elite_preventive'
                  planName='elite_preventive'
                  planLabel='Preventive'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite') && selectedPlans.includes('cigna_care_connect_preventive')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_elite_dental'
                  planName='elite_dental'
                  planLabel='Dental'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite') && selectedPlans.includes('cigna_care_connect_dental')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_elite_vision'
                  planName='elite_vision'
                  planLabel='Vision'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_elite') && selectedPlans.includes('cigna_care_connect_vision')}
                />
                {
                  selectedPlans.includes('cigna_care_connect_elite') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#dfe3ec' }}>Total Premium Incl. GST {gst * 100}%</TableCell>
                    {
                      clients.map((client, idx) => {
                        return <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx, 'elite'))}</TableCell>
                      })
                    }
                    <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremium('elite'))}</TableCell>
                  </TableRow>
                }



                {
                  selectedPlans.includes('cigna_care_connect_executive') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#b9f6ca', color: '#000' }}>Executive</TableCell>
                  </TableRow>
                }

                <TableRatesRow
                  planId='cigna_care_connect_executive_inpatient'
                  planName='executive_inpatient'
                  planLabel='Inpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_executive_outpatient'
                  planName='executive_outpatient'
                  planLabel='Outpatient'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive') && selectedPlans.includes('cigna_care_connect_outpatient')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_executive_maternity'
                  planName='executive_maternity'
                  planLabel='Maternity'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive') && selectedPlans.includes('cigna_care_connect_maternity')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_executive_preventive'
                  planName='executive_preventive'
                  planLabel='Preventive'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive') && selectedPlans.includes('cigna_care_connect_preventive')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_executive_dental'
                  planName='executive_dental'
                  planLabel='Dental'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive') && selectedPlans.includes('cigna_care_connect_dental')}
                />
                <TableRatesRow
                  planId='cigna_care_connect_executive_vision'
                  planName='executive_vision'
                  planLabel='Vision'
                  clients={clients}
                  selectedPlans={selectedPlans}
                  loading={loadingStatus[id]}
                  rates={response ? computeCignaCareConnectRates(response.rates) : []}
                  currency={currency}
                  discount={discounts[id]}
                  additionalLoading={loadings[id]}
                  visible={selectedPlans.includes('cigna_care_connect_executive') && selectedPlans.includes('cigna_care_connect_vision')}
                />
                {
                  selectedPlans.includes('cigna_care_connect_executive') &&
                  <TableRow style={{ backgroundColor: '#ffffff' }}>
                    <TableCell style={{ border: '2px solid #fff', fontWeight: 600, background: '#dfe3ec' }}>Total Premium Incl. GST {gst * 100}%</TableCell>
                    {
                      clients.map((client, idx) => {
                        return <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }} key={idx}> {loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremiumPerClient(idx, 'executive'))}</TableCell>
                      })
                    }
                    <TableCell style={{ border: '2px solid #fff', background: '#dfe3ec' }}>{loadingStatus[id] ? <Skeleton variant="rounded" width={100} height={20} /> : formatRate(computeTotalPremium('executive'))}</TableCell>
                  </TableRow>
                }
              </TableBody>
            </Table>

          </TableContainer>

        
        <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>


      </Paper>

    </Box>
  );
});

export default CignaCareConnect_Rates;